import React, { useEffect, useRef, useState } from "react";

/**
 * Lightweight WebGL aurora — a single full-screen fragment shader that paints
 * drifting "northern lights" bands in the brand palette. Additive-blended over
 * the dark hero so it reads as a glow.
 *
 * Deliberately NOT three.js (keeps the bundle lean). Client-only: it mounts
 * after hydration, so prerendered HTML is unaffected. Falls back to nothing
 * (the CSS <AuroraBackground> stays visible underneath) when WebGL is
 * unavailable or the user prefers reduced motion.
 */

const FRAG = `
precision mediump float;
uniform vec2 u_res;
uniform float u_time;

float hash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453123); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  float a = hash(i), b = hash(i + vec2(1.0, 0.0));
  float c = hash(i + vec2(0.0, 1.0)), d = hash(i + vec2(1.0, 1.0));
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
}
float fbm(vec2 p){
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 5; i++){ v += a * noise(p); p *= 2.02; a *= 0.5; }
  return v;
}

void main(){
  vec2 uv = gl_FragCoord.xy / u_res.xy;
  vec2 p = vec2(uv.x * (u_res.x / u_res.y), uv.y);
  float t = u_time * 0.04;

  float flow = fbm(vec2(p.x * 1.4 + t, p.y * 2.2 - t * 0.6));
  // Curtains: vertical streaks that ripple sideways over time.
  float curtain = fbm(vec2(p.x * 3.0 + sin(t + p.y * 3.0) * 0.4, p.y * 1.2 + t * 0.3));
  float band = smoothstep(0.22, 0.82, flow * 0.7 + curtain * 0.7);
  // Concentrate toward the top, fade out at the bottom.
  band *= smoothstep(0.0, 0.92, 1.0 - uv.y);

  vec3 violet = vec3(0.30, 0.16, 0.45);
  vec3 cyan   = vec3(0.306, 0.847, 0.902);
  vec3 rose   = vec3(0.953, 0.647, 0.753);
  vec3 gold   = vec3(0.953, 0.765, 0.353);

  vec3 col = mix(violet, cyan, smoothstep(0.2, 0.85, fbm(p * 1.2 + vec2(t, 0.0))));
  col = mix(col, rose, 0.45 * fbm(p * 2.0 - vec2(0.0, t)));
  col = mix(col, gold, 0.5 * band);

  float alpha = band * 0.9;
  gl_FragColor = vec4(col * alpha, alpha);
}
`;

const VERT = `
attribute vec2 a_pos;
void main(){ gl_Position = vec4(a_pos, 0.0, 1.0); }
`;

function compile(gl, type, src) {
  const sh = gl.createShader(type);
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
    gl.deleteShader(sh);
    return null;
  }
  return sh;
}

export const AuroraShaderCanvas = ({ className = "" }) => {
  const canvasRef = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    let gl;
    try {
      gl =
        canvas.getContext("webgl", { premultipliedAlpha: true, alpha: true, antialias: false }) ||
        canvas.getContext("experimental-webgl");
    } catch (_) {
      return undefined;
    }
    if (!gl) return undefined;

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return undefined;

    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return undefined;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, "a_pos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uRes = gl.getUniformLocation(prog, "u_res");
    const uTime = gl.getUniformLocation(prog, "u_time");

    gl.enable(gl.BLEND);
    gl.blendFunc(gl.ONE, gl.ONE); // additive glow

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, canvas.width, canvas.height);
      gl.uniform2f(uRes, canvas.width, canvas.height);
    };

    let raf = 0;
    let running = true;
    const start = performance.now();
    const render = () => {
      if (!running) return;
      resize();
      gl.uniform1f(uTime, (performance.now() - start) / 1000);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
      raf = requestAnimationFrame(render);
    };

    // Pause when the tab/section is offscreen to save GPU.
    const onVisibility = () => {
      running = !document.hidden;
      if (running) {
        cancelAnimationFrame(raf);
        raf = requestAnimationFrame(render);
      }
    };
    document.addEventListener("visibilitychange", onVisibility);

    setActive(true);
    raf = requestAnimationFrame(render);

    return () => {
      running = false;
      cancelAnimationFrame(raf);
      document.removeEventListener("visibilitychange", onVisibility);
      const lose = gl.getExtension("WEBGL_lose_context");
      if (lose) lose.loseContext();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      className={`aurora-shader ${active ? "is-active" : ""} ${className}`.trim()}
    />
  );
};
